import {
	Button,
	Card,
	H1,
	H2,
	H3,
	Icon,
	Input,
	LineButton,
	Link,
	P,
	Spinner,
	styled,
	T,
	th,
	useLinks,
	v,
	Notification,
	NotificationText,
} from "@veracity/vui";
import * as yup from "yup";
import { Formik } from "formik";
import React, { useEffect, useState } from "react";

import { Page } from "../components/Page";
import { showErrorDialogAsync } from "../showErrorDialogAsync";
import { Ids } from "../components/Ids";
import { Label } from "../components/Label";
import { InputHelpText } from "../components/InputHelpText";
import { VeracityByDNV } from "../components/VeracityByDNV";
import { DNV } from "../components/DNV";
import { PaperAirplane } from "../components/PaperAirplane";
import { fetchVeracityVoid, ServerError } from "../services/fetchVeracity";
import { getCurrentReturnUrl } from "../utils/getCurrentReturnUrl";
import { identityClient } from "..";
import { PasswordValidationResult } from "../IdentityClient";
import { CancellationToken } from "../utils/CancellationToken";
import { useSearchParams } from "react-router-dom";
import { ForgotPasswordService } from "../services/auto-generated/ForgotPasswordService";
import { ValidatedForgotPasswordRequestDto } from "../models/auto-generated/ValidatedForgotPasswordRequestDto";
import { delayAsync } from "../utils/delayAsync";
import { PasswordInput } from "../components/PasswordInput";
import { PasswordStrengthMeter } from "../components/PasswordStrengthMeter";
import { ValidationService } from "../services/auto-generated/ValidationService";
import { getUrlWithLoggedInParameterSet } from "../utils/getUrlWithLoggedInParameterSet";
import { PasswordEntropyDto } from "../models/auto-generated/PasswordEntropyDto";
import { LostTotpService } from "../services/auto-generated/LostTotpService";
import { ValidatedLostTotpRequestDto } from "../models/auto-generated/ValidatedLostTotpRequestDto";

const SLIDE_WIDTH = 387;

export function ResetTotpPage() {
	let [state, setState] = useState<{
		step: ResetTotpStep;
		token?: string;
		validateLostTotpRequest?: ValidatedLostTotpRequestDto;
		returnUrl?: string;
		error?;
	}>({ step: ResetTotpStep.FetchingValidatedLostTotpRequest });

	if (state.step === ResetTotpStep.Error) throw state.error;

	let [searchParams] = useSearchParams();
	let [links] = useLinks();

	useEffect(() => {
		let token = searchParams.get("token");
		let ct = new CancellationToken();

		(async () => {
			try {
				var validateLostTotpRequest =
					await ForgotPasswordService.validateRequest(token);

				ct.throwIfCancellationRequested();

				if (IS_DEVELOPMENT)
					console.debug(
						"validateForgotPasswordRequest",
						validateLostTotpRequest
					);
			} catch (e) {
				if (ct.isCancellationRequested) return;

				if (IS_DEVELOPMENT) console.debug(e);

				setState({
					step: ResetTotpStep.Error,
					error: e,
				});

				return;
			}

			setState({
				step: ResetTotpStep.FetchedValidatedLostTotpRequest,
				token,
				validateLostTotpRequest,
			});
		})();

		return ct.cancel.bind(ct);
	}, []);

	if (state.step === ResetTotpStep.FetchingValidatedLostTotpRequest)
		return (
			<Page
				key="FETCHING_VALIDATE_FORGOT_PASSWORD_REQUEST"
				center
				bg="skyBlue.97"
			>
				<Spinner text="Please wait..." />
			</Page>
		);

	return (
		<Page p={1} bg="skyBlue.97" center>
			<Card
				column
				w={SLIDE_WIDTH}
				overflow="hidden"
				border="1px solid grey.40"
				userSelect="none"
			>
				<Slides
					style={{
						left:
							state.step === ResetTotpStep.YourTotpHasBeenReset
								? -SLIDE_WIDTH + "px"
								: "0",
					}}
				>
					<v.div w={SLIDE_WIDTH} p="32px">
						<H3 as="h1">Reset TOTP</H3>
						<Label mt={4}>Email address</Label>
						<T>{state.validateLostTotpRequest.Email}</T>
						<P>
							This action will reset your current TOTP enrollment
							and set your 2FA method to SMS/Call.
						</P>
						<Formik
							initialValues={{
								emailAddress:
									state.validateLostTotpRequest.Email,
								newPassword: "",
								passwordConfirmation: "",
							}}
							onSubmit={async (values, { setFieldError }) => {
								try {
									await LostTotpService.resetTotp(
										state.validateLostTotpRequest.UserId,
										state.token
									);
								} catch (e) {
									showErrorDialogAsync(
										"Error when resetting TOTP",
										e
									);

									return;
								}

								// Determine the return url

								var returnUrl = getCurrentReturnUrl();

								if (returnUrl) {
									try {
										returnUrl = (
											await ValidationService.sanitize({
												Url: returnUrl,
											})
										).Url; // Don't trust what's in the browser url
									} catch (e) {
										console.error(e);
										returnUrl = links.services;
									}
								}

								if (!returnUrl) returnUrl = links.services;
								const loggedInString = "?loggedin=1";
								if (returnUrl.endsWith(loggedInString))
									returnUrl = returnUrl.slice(
										0,
										returnUrl.length - loggedInString.length
									);

								returnUrl =
									getUrlWithLoggedInParameterSet(returnUrl);

								if (IS_DEVELOPMENT)
									console.debug("returnUrl", returnUrl);

								setState({
									step: ResetTotpStep.YourTotpHasBeenReset,
									validateLostTotpRequest:
										state.validateLostTotpRequest,
									returnUrl,
								});
							}}
						>
							{({
								values,
								errors,
								touched,
								isSubmitting,
								handleSubmit,
								getFieldProps,
								setFieldValue,
							}) => {
								return (
									<form noValidate onSubmit={handleSubmit}>
										<v.fieldset
											p={0}
											disabled={
												isSubmitting ||
												state.step ===
													ResetTotpStep.YourTotpHasBeenReset
											}
										>
											<Notification
												mt={2}
												variant="subtleBlue"
											>
												<NotificationText
													mx={1.5}
													my={1.5}
												>
													Please allow for up to five
													minutes for changes to take
													effect
												</NotificationText>
											</Notification>
											<Button
												mt={3}
												type="submit"
												iconRight="falArrowRight"
												isLoading={isSubmitting}
											>
												Confirm
											</Button>
										</v.fieldset>
									</form>
								);
							}}
						</Formik>
					</v.div>
					<v.div
						w={SLIDE_WIDTH}
						p={4}
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
						textAlign="center"
					>
						<Icon
							name="falCheckCircle"
							color="green.80"
							scale={3}
						/>
						<H3 as="h1" mt={2}>
							Your TOTP authentication has been reset
						</H3>
						<Notification mt={2} variant="subtleBlue">
							<NotificationText mx={1.5} my={1.5}>
								Please allow for up to five minutes for changes
								to take effect
							</NotificationText>
						</Notification>
						<Button
							mt={2}
							as="a"
							href={state.returnUrl}
							iconRight="falArrowRight"
						>
							Continue to sign in
						</Button>
					</v.div>
				</Slides>
				<v.footer bg="hsl(223, 67%, 18%)" p="24px 32px 32px" color="white">
					<v.div display="flex" justifyContent="space-between">
						<VeracityByDNV />
						<DNV />
					</v.div>
					<P mt={2} fontSize="12px">
						Veracity is DNV's independent data platform and industry
						eco-system.
					</P>
					<P fontSize="12px">
						Visit{" "}
						<Link fontSize="12px" href={links.home} color="blue.40">
							www.dnv.com
						</Link>{" "}
						to discover more.
					</P>
				</v.footer>
			</Card>
		</Page>
	);
}

const Slides = styled.divBox`
	display: flex;
	position: relative;
	transition: left ${th.animation("standard")} 0.5s;
	width: ${SLIDE_WIDTH * 2}px;
`;

enum ResetTotpStep {
	FetchingValidatedLostTotpRequest,
	Error,
	FetchedValidatedLostTotpRequest,
	YourTotpHasBeenReset,
}
